import React, { useState, useEffect } from 'react';
import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import api from '../../utils/api';
import PackageInquiryModal from '../../components/modals/PackageInquiryModal';

const dummyData = [
  { 
    name: 'Free', 
    color: '#22c55e', 
    icon: 'fa-leaf', 
    price: '0', 
    period: '15 Days',
    tagline: 'Get started with no cost',
    categories: [
      {
        title: 'Property Listing',
        features: ['1 Property Listing', '1 Property Type', 'Limited Edit Listing']
      },
      {
        title: 'Media Features',
        features: ['3 Images Upload', 'No YouTube Link ❌', 'No Floor Plan Upload ❌']
      },
      {
        title: 'Visibility & Boost',
        features: ['Low Listing Visibility', 'No Featured Listing ❌', 'No Urgent Sale Tag ❌', 'Low Search Priority']
      },
      {
        title: 'Leads & Contact',
        features: ['Limited Buyer Inquiries', 'No WhatsApp Button ❌', 'No Direct Call Visibility ❌', 'No Lead Notifications ❌']
      },
      {
        title: 'Contact Control',
        features: ['Limited Show to Brokers', 'Contact Unlock Enabled ✅']
      },
      {
        title: 'Owner Dashboard',
        features: ['Add/Edit Property ✅', 'No View Leads ❌', 'No Property Performance ❌', 'No Listing Renewal ❌']
      }
    ]
  },
  { 
    name: 'Basic', 
    color: '#3b82f6', 
    icon: 'fa-star', 
    price: '299', 
    period: '30 Days',
    tagline: 'Essential features for owners',
    popular: true,
    categories: [
      {
        title: 'Property Listing',
        features: ['2 Property Listings', '2 Property Types', 'Full Edit Listing ✅']
      },
      {
        title: 'Media Features',
        features: ['10 Images Upload', 'No YouTube Link ❌', 'No Floor Plan Upload ❌']
      },
      {
        title: 'Visibility & Boost',
        features: ['Medium Listing Visibility', 'No Featured Listing ❌', 'No Urgent Sale Tag ❌', 'Medium Search Priority']
      },
      {
        title: 'Leads & Contact',
        features: ['Standard Buyer Inquiries', 'WhatsApp Button ✅', 'Limited Direct Call Visibility', 'SMS Lead Notifications']
      },
      {
        title: 'Contact Control',
        features: ['Limited Show to Brokers', 'Contact Unlock Enabled ✅']
      },
      {
        title: 'Owner Dashboard',
        features: ['Add/Edit Property ✅', 'Basic View Leads', 'No Property Performance ❌', 'Listing Renewal Option ✅']
      }
    ]
  },
  { 
    name: 'Premium', 
    color: '#8b5cf6', 
    icon: 'fa-fire', 
    price: '999', 
    period: '45 Days',
    tagline: 'Maximize your property reach',
    categories: [
      {
        title: 'Property Listing',
        features: ['5 Property Listings', 'Unlimited Property Types', 'Full Edit Listing ✅']
      },
      {
        title: 'Media Features',
        features: ['25 Images Upload', '1 YouTube Video Link', 'Floor Plan Upload ✅']
      },
      {
        title: 'Visibility & Boost',
        features: ['High Listing Visibility', '7 Days Featured Listing', 'Urgent Sale Tag ✅', 'High Search Priority']
      },
      {
        title: 'Leads & Contact',
        features: ['Priority Buyer Inquiries', 'WhatsApp Button ✅', 'Full Direct Call Visibility', 'SMS + Email Notifications']
      },
      {
        title: 'Contact Control',
        features: ['Full Show to Brokers', 'Contact Unlock Enabled ✅']
      },
      {
        title: 'Owner Dashboard',
        features: ['Add/Edit Property ✅', 'Standard View Leads', 'Property Performance (Views) ✅', 'Listing Renewal Option ✅']
      }
    ]
  },
  { 
    name: 'Elite', 
    color: '#ef4444', 
    icon: 'fa-rocket', 
    price: '2,499', 
    period: '60 Days',
    tagline: 'The ultimate selling toolkit',
    categories: [
      {
        title: 'Property Listing',
        features: ['10 Property Listings', 'Unlimited Property Types', 'Full Edit Listing ✅']
      },
      {
        title: 'Media Features',
        features: ['50 Images Upload', '2 YouTube Video Links', 'Floor Plan Upload ✅']
      },
      {
        title: 'Visibility & Boost',
        features: ['Highest Listing Visibility', '15 Days Featured Listing', 'Urgent Sale Tag ✅', 'Top Search Priority']
      },
      {
        title: 'Leads & Contact',
        features: ['Priority+ Buyer Inquiries', 'WhatsApp Button ✅', 'Full Direct Call Visibility', 'Instant Alert Notifications']
      },
      {
        title: 'Contact Control',
        features: ['Full Show to Brokers', 'Contact Unlock Enabled ✅']
      },
      {
        title: 'Owner Dashboard',
        features: ['Add/Edit Property ✅', 'Advanced View Leads', 'Property Performance (Views) ✅', 'Listing Renewal Option ✅']
      }
    ]
  },
];

export default function OwnerPackages() {
  const [packages, setPackages] = useState(dummyData);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<{ name: string; type: string } | null>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await api.getPackages('owner');
        if (data && data.length > 0) {
          setPackages(data);
        }
      } catch (err) {
        console.error('Failed to fetch dynamic owner packages, using dummy data.');
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const cleanTitle = (title: string) => {
    let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    const mapping: Record<string, string> = {
      'PROPERTY LISTING': 'LISTING',
      'MEDIA FEATURES': 'MEDIA',
      'VISIBILITY & BOOST': 'BOOST',
      'LEADS & CONTACT': 'LEADS',
      'CONTACT CONTROL': 'VISIBILITY',
      'OWNER DASHBOARD': 'DASHBOARD'
    };
    for (const [key, value] of Object.entries(mapping)) {
      if (cleaned.toUpperCase().includes(key)) return value;
    }
    return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
  };

  const cleanFeature = (text: string) => {
    return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
  };

  return (
    <div className="bg-[#F4F6F9] min-h-screen font-sans overflow-x-hidden">
      <Navbar transparent={false} />

      <PackageInquiryModal 
        isOpen={!!selectedPackage}
        onClose={() => setSelectedPackage(null)}
        packageName={selectedPackage?.name || ''}
        packageType="Owner"
      />

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 overflow-hidden bg-[#0F2A5F]">
        <div className="absolute inset-0 z-0 overflow-hidden">
           <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
           <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/20 text-[#D4A017] text-[10px] font-bold uppercase tracking-[0.3em] mb-8 shadow-xl">
            <i className="fa-solid fa-user-tag opacity-70"></i>
            Owner Exclusive Packages
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight leading-tight uppercase text-center w-full">
            Sell Property <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4A017] to-[#f3cf7a]">Like a Pro</span>
          </h1>
          <p className="max-w-2xl mx-auto text-[#ffbb00]/60 text-base md:text-lg font-medium leading-relaxed uppercase tracking-widest text-center">
            Simple, smart, and powerful plans designed for individual property owners. Reach thousands of buyers and brokers instantly.
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="relative z-20 -mt-10 pb-32">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
             <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A017]"></div>
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
              {packages.map((pkg, idx) => (
                <div 
                  key={pkg.name} 
                  className={`group relative flex flex-col bg-white rounded-[2.5rem] shadow-xl border border-[#D4A017]/10 hover:border-[#D4A017]/40 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${pkg.popular ? 'scale-[1.02] z-10 border-[#D4A017]/50' : ''}`}
                >
                  {/* Popular Tag */}
                  {pkg.popular && (
                    <div className="absolute top-6 -right-12 bg-[#D4A017] text-white text-[8px] font-black uppercase tracking-widest px-14 py-2 rotate-45 shadow-lg z-10">
                      Smart Choice
                    </div>
                  )}

                  {/* Header Area */}
                  <div className="p-8 border-b border-[#05144b]/5 relative overflow-hidden text-white bg-[#0F2A5F]">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg shadow-lg mb-6 group-hover:scale-110 transition-transform duration-500" style={{ backgroundColor: `${pkg.color}25`, color: pkg.color }}>
                      <i className={`fa-solid ${pkg.icon}`}></i>
                    </div>
                    
                    <h3 className="text-xl font-black uppercase tracking-tight mb-1 text-[#ffbb00] truncate">{pkg.name}</h3>
                    <p className="text-[10px] font-black text-white/40 mb-6 line-clamp-1 uppercase tracking-widest">{pkg.tagline}</p>
                    
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black tracking-tighter text-white">
                        ₹{pkg.price}
                      </span>
                      <span className="text-[10px] font-black text-white/30 uppercase tracking-widest">
                        / {pkg.period}
                      </span>
                    </div>
                  </div>

                  {/* Features Area (Detailed) */}
                  <div className="p-8 flex-grow bg-white">
                    <div className="space-y-8">
                      {pkg.categories.map((cat, ci) => (
                        <div key={ci} className="mb-6 last:mb-0">
                          <h4 className="text-[10px] font-black text-[#05144b] uppercase tracking-[0.2em] mb-4 border-b border-[#05144b]/5 pb-2 flex items-center gap-2">
                             <div className="w-1.5 h-1.5 rounded-full bg-[#ffbb00]"></div>
                             {cleanTitle(cat.title)}
                          </h4>
                          <ul className="space-y-3.5 px-1">
                            {cat.features.map((feat, fi) => {
                              const isAvailable = !feat.includes('❌') && !feat.toLowerCase().includes('no ');
                              const cleanText = cleanFeature(feat);
                              return (
                                <li key={fi} className={`flex items-start gap-3 ${isAvailable ? '' : 'opacity-10 grayscale'}`}>
                                  <i className={`fa-solid ${isAvailable ? 'fa-circle-check text-[#D4A017]' : 'fa-circle-xmark text-[#05144b]/20'} text-[11px] mt-0.5`}></i>
                                  <span className="text-[13px] font-black text-[#05144b] leading-snug uppercase tracking-tight">
                                    {cleanText}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer Area */}
                  <div className="p-6 bg-white">
                    <button 
                      onClick={() => setSelectedPackage({ name: pkg.name, type: 'owner' })}
                      className={`w-full py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 ${pkg.popular ? 'bg-[#D4A017] text-white hover:bg-[#8b4513]' : 'bg-[#0F2A5F] text-white hover:bg-black'}`}
                    >
                      Select Plan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Support Section */}
      <div className="mt-24 max-w-4xl mx-auto text-center animate__animated animate__fadeIn pb-32">
        <div className="p-12 rounded-[3.5rem] bg-gradient-to-br from-[#0F2A5F] to-blue-950 text-white relative overflow-hidden shadow-2xl shadow-blue-900/40">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-tight">Need Help Choosing?</h2>
            <p className="text-white/40 text-lg mb-10 font-medium leading-relaxed max-w-2xl mx-auto uppercase tracking-wider">
              Our property experts are here to help you find the best plan to sell your property faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-10 py-5 bg-[#D4A017] text-white rounded-2xl font-black uppercase tracking-widest hover:bg-[#8b4513] transition-all shadow-xl shadow-[#D4A017]/20">
                Contact Support
              </button>
              <button className="px-10 py-5 bg-white/5 border border-white/10 backdrop-blur-md text-white rounded-2xl font-black uppercase tracking-widest hover:bg-white/15 transition-all">
                Owner FAQ
              </button>
            </div>
          </div>
        </div>
      </div>

      <FooterTop bg="bg-white" />
      <Footer />
    </div>
  );
}
